import { formatTimeTo12Hour } from '../../../utils/datetime/datetime-comprehensive';
import { DateTime } from 'luxon';

export const normalizeTimeString = (value) => {
  if (!value) return null;

  // Already HH:mm or HH:mm:ss
  if (/^\d{2}:\d{2}(:\d{2})?$/.test(value)) {
    return value.length === 5 ? `${value}:00` : value;
  }

  // ISO datetime fallback
  const asDate = new Date(value);
  if (!Number.isNaN(asDate.getTime())) {
    return asDate.toLocaleTimeString('en-US', {
      timeZone: 'America/Chicago',
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }

  return null;
};

export const getCheckinWindowTimes = (session) => {
  if (!session) {
    return { start: null, end: null };
  }

  // Prefer explicit check-in window, otherwise fall back to session window
  const rawStart = session.checkin_start_time || session.start_time;
  const rawEnd = session.checkin_end_time || session.end_time;

  return {
    start: normalizeTimeString(rawStart),
    end: normalizeTimeString(rawEnd),
    rawStart,
    rawEnd
  };
};

// Chicago-timezone-aware, calendar-day-boundary-safe check-in window
// calculation. Extracted VERBATIM from useAttendanceCheckIn (parameterized
// on session/nowTick instead of closing over hook state).
export const getTimeWindowStatus = (session, nowTick) => {
  if (!session) {
    return null;
  }

  const {
    start: checkinStart,
    end: checkinEnd,
    rawStart,
    rawEnd
  } = getCheckinWindowTimes(session);

  if (!checkinStart || !checkinEnd) {
    return null;
  }

  const parseTimeToSeconds = (timeValue) => {
    if (!timeValue) return null;
    const normalized = normalizeTimeString(timeValue);
    if (!normalized) return null;
    const [h, m, s] = normalized.split(':').map(Number);
    if ([h, m, s].some(Number.isNaN)) return null;
    return (h * 3600) + (m * 60) + s;
  };

  const getChicagoDateKey = (dateValue) => {
    if (!dateValue) return null;
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return dateValue;
    }

    const parsed = new Date(dateValue);
    if (Number.isNaN(parsed.getTime())) return null;

    return parsed.toLocaleDateString('en-CA', {
      timeZone: 'America/Chicago',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const dateKeyToDayIndex = (dateKey) => {
    const ts = Date.parse(`${dateKey}T00:00:00Z`);
    if (Number.isNaN(ts)) return null;
    return Math.floor(ts / 86400000);
  };

  const getDateKeyForBoundary = (rawBoundaryValue, fallbackDateKey) => {
    // Datetime value (ISO/SQL-like) carries its own calendar day.
    if (typeof rawBoundaryValue === 'string' && rawBoundaryValue.includes('T')) {
      const fromDateTime = getChicagoDateKey(rawBoundaryValue);
      if (fromDateTime) return fromDateTime;
    }

    // Time-only values must use the session date fallback.
    return fallbackDateKey || null;
  };

  // Get current time/date in Chicago timezone with second-level precision
  const chicagoNow = DateTime.fromMillis(nowTick).setZone('America/Chicago');
  const currentTime = chicagoNow.toFormat('HH:mm:ss');
  const currentDateKey = chicagoNow.toFormat('yyyy-MM-dd');
  const sessionDateKey = getChicagoDateKey(session?.date || session?.start_time);
  const startDateKey = getDateKeyForBoundary(rawStart, sessionDateKey);
  const endDateKey = getDateKeyForBoundary(rawEnd, startDateKey);

  const formatDurationHms = (totalSeconds) => {
    const safeSeconds = Math.max(0, Math.floor(totalSeconds));
    const hours = Math.floor(safeSeconds / 3600);
    const minutes = Math.floor((safeSeconds % 3600) / 60);
    const seconds = safeSeconds % 60;

    const parts = [];
    if (hours > 0) parts.push(`${hours} hour${hours === 1 ? '' : 's'}`);
    if (minutes > 0 || hours > 0) parts.push(`${minutes} minute${minutes === 1 ? '' : 's'}`);
    parts.push(`${seconds} second${seconds === 1 ? '' : 's'}`);

    if (parts.length === 1) return parts[0];
    if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
    return `${parts[0]}, ${parts[1]}, and ${parts[2]}`;
  };

  const formatDateLabel = (dateKey) => {
    if (!dateKey) return null;

    const dateObj = new Date(`${dateKey}T12:00:00`);
    if (Number.isNaN(dateObj.getTime())) return null;

    return dateObj.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const currentSeconds = parseTimeToSeconds(currentTime);
  const startSeconds = parseTimeToSeconds(checkinStart);
  const endSeconds = parseTimeToSeconds(checkinEnd);
  const currentDayIndex = dateKeyToDayIndex(currentDateKey);
  const startDayIndex = startDateKey ? dateKeyToDayIndex(startDateKey) : null;
  const endDayIndex = endDateKey ? dateKeyToDayIndex(endDateKey) : null;

  if (
    currentSeconds !== null &&
    startSeconds !== null &&
    endSeconds !== null &&
    currentDayIndex !== null &&
    startDayIndex !== null &&
    endDayIndex !== null
  ) {
    const currentAbsolute = (currentDayIndex * 86400) + currentSeconds;
    const startAbsolute = (startDayIndex * 86400) + startSeconds;
    const endAbsolute = (endDayIndex * 86400) + endSeconds;

    if (currentAbsolute < startAbsolute) {
      const secondsUntilStart = startAbsolute - currentAbsolute;
      return {
        type: 'before',
        message: `Check-in opens in ${formatDurationHms(secondsUntilStart)}${formatDateLabel(startDateKey) ? ` (on ${formatDateLabel(startDateKey)} at ${formatTimeTo12Hour(rawStart || checkinStart)} CT)` : ''}`,
        startTime: formatTimeTo12Hour(rawStart || checkinStart),
        endTime: formatTimeTo12Hour(rawEnd || checkinEnd)
      };
    }

    if (currentAbsolute > endAbsolute) {
      const closedDateLabel = formatDateLabel(endDateKey);
      const closedAt = formatTimeTo12Hour(rawEnd || checkinEnd);
      return {
        type: 'after',
        message: closedDateLabel
          ? `Check-in window closed on ${closedDateLabel} at ${closedAt} CT`
          : `Check-in window closed at ${closedAt} CT`,
        startTime: formatTimeTo12Hour(rawStart || checkinStart),
        endTime: formatTimeTo12Hour(rawEnd || checkinEnd)
      };
    }
  } else if (currentTime < checkinStart) {
    // Fallback to time-only comparison if date parsing fails.
    const startTime = new Date(`2000-01-01T${checkinStart}`);
    const currentTimeObj = new Date(`2000-01-01T${currentTime}`);
    const secondsUntilStart = Math.ceil((startTime - currentTimeObj) / 1000);

    return {
      type: 'before',
      message: `Check-in opens in ${formatDurationHms(secondsUntilStart)}${formatDateLabel(startDateKey) ? ` (on ${formatDateLabel(startDateKey)} at ${formatTimeTo12Hour(rawStart || checkinStart)} CT)` : ''}`,
      startTime: formatTimeTo12Hour(rawStart || checkinStart),
      endTime: formatTimeTo12Hour(rawEnd || checkinEnd)
    };
  } else if (currentTime > checkinEnd) {
    // Fallback to time-only comparison if date parsing fails.
    const closedDateLabel = formatDateLabel(endDateKey || currentDateKey);
    const closedAt = formatTimeTo12Hour(rawEnd || checkinEnd);

    return {
      type: 'after',
      message: closedDateLabel
        ? `Check-in window closed on ${closedDateLabel} at ${closedAt} CT`
        : `Check-in window closed at ${closedAt} CT`,
      startTime: formatTimeTo12Hour(rawStart || checkinStart),
      endTime: formatTimeTo12Hour(rawEnd || checkinEnd)
    };
  }

  return {
    type: 'available',
    message: 'Check-in is available now',
    startTime: formatTimeTo12Hour(rawStart || checkinStart),
    endTime: formatTimeTo12Hour(rawEnd || checkinEnd)
  };
};
